import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export type BulkCodeValidationStatus =
  | 'Pending'
  | 'Running'
  | 'Paused'
  | 'Completed'
  | 'Failed'
  | 'Cancelled';

export interface BulkCodeValidationPreview {
  revalidateAfterDays: number;
  defaultBranch: string;
  excludedKeyReasons: string[];
  availableKeyReasons: string[];
  gitHubApplicationCount: number;
  eligibleCount: number;
  eligibleRepositoryCount: number;
  skippedRecentlyValidatedCount: number;
  skippedExcludedCount: number;
  hasGitHubPat: boolean;
  gitHubPatExpiresAt: string | null;
  sampleApplicationNames: string[];
}

export interface BulkCodeValidationRun {
  id: number;
  status: BulkCodeValidationStatus;
  requestedByEmail: string | null;
  revalidateAfterDays: number;
  defaultBranch: string;
  excludedKeyReasons: string[];
  totalApplications: number;
  totalRepositories: number;
  processedCount: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  excludedCount: number;
  conflictCount: number;
  currentApplicationName: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
}

export interface BulkCodeValidationRunJob {
  id: number;
  status: number | string;
  jobType: number | string;
  repositoryUrl: string;
  branch: string;
  orionId: string | null;
  trackedBusinessApplicationId: number | null;
  createdAt: string;
  completedAt: string | null;
  errorMessage: string | null;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  lightweightResult: {
    score: number | null;
    cmdbComparison: string;
    recommendedAction: string;
    message: string | null;
  } | null;
}

export interface PagedResult<T> {
  data: T[];
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

@Injectable({ providedIn: 'root' })
export class BulkCodeValidationService {
  private readonly baseUrl = environment.apiUrl + '/admin/code-validation/bulk';

  constructor(private readonly http: HttpClient) {}

  getPreview(revalidateAfterDays?: number, defaultBranch?: string, excludedKeyReasons?: string[]): Observable<BulkCodeValidationPreview> {
    let params = new HttpParams();
    if (revalidateAfterDays != null) {
      params = params.set('revalidateAfterDays', revalidateAfterDays);
    }
    if (defaultBranch) {
      params = params.set('defaultBranch', defaultBranch);
    }
    if (excludedKeyReasons?.length) {
      params = params.set('excludedKeyReasons', excludedKeyReasons.join(','));
    }
    return this.http.get<BulkCodeValidationPreview>(`${this.baseUrl}/preview`, { params });
  }

  start(revalidateAfterDays?: number, defaultBranch?: string, excludedKeyReasons?: string[]): Observable<BulkCodeValidationRun> {
    return this.http.post<BulkCodeValidationRun>(`${this.baseUrl}/start`, {
      revalidateAfterDays,
      defaultBranch,
      excludedKeyReasons
    });
  }

  pause(runId: number): Observable<BulkCodeValidationRun> {
    return this.http.post<BulkCodeValidationRun>(`${this.baseUrl}/${runId}/pause`, {});
  }

  resume(runId: number): Observable<BulkCodeValidationRun> {
    return this.http.post<BulkCodeValidationRun>(`${this.baseUrl}/${runId}/resume`, {});
  }

  cancel(runId: number): Observable<BulkCodeValidationRun> {
    return this.http.post<BulkCodeValidationRun>(`${this.baseUrl}/${runId}/cancel`, {});
  }

  getStatus(): Observable<BulkCodeValidationRun | null> {
    return this.http.get<BulkCodeValidationRun | null>(`${this.baseUrl}/status`);
  }

  getHistory(page = 1, pageSize = 20): Observable<PagedResult<BulkCodeValidationRun>> {
    const params = new HttpParams().set('page', page).set('pageSize', pageSize);
    return this.http.get<PagedResult<BulkCodeValidationRun>>(`${this.baseUrl}/history`, { params });
  }

  getRunJobs(runId: number, page = 1, pageSize = 20, outcome?: string): Observable<PagedResult<BulkCodeValidationRunJob>> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (outcome && outcome !== 'all') {
      params = params.set('outcome', outcome);
    }
    return this.http.get<PagedResult<BulkCodeValidationRunJob>>(`${this.baseUrl}/${runId}/jobs`, { params });
  }}
